import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {textInUserLanguage} from '../questions/Questions';
import {Languages} from '../questions/questions-store';

export const DisableQuestionModal = observer((): JSX.Element => {
    const { questionStore: store, userStore } = useContext(StoreContext);
    const { businessUnits } = userStore.payload!;
    const { catalogQuestionIdInEditing: questionId } = store.variables;

    const categoryContainingQuestion = store.payload?.allQuestionsWithCategories.find((category) =>
        category.allCatalogQuestions.some((question) => question.id === questionId));

    const disabledBusinessUnitsFromStore = categoryContainingQuestion?.disabledBusinessUnits || [];

    const allBusinessUnitIds = businessUnits.map((unit) => unit.id);

    const availableBusinessUnits = allBusinessUnitIds.filter(
        (id) => !disabledBusinessUnitsFromStore.includes(id));

    const allCatalogQuestions = store.payload?.allQuestionsWithCategories
        .map((category) => category.allCatalogQuestions)
        .reduce((acc, questions) => acc.concat(questions), []);

    const question = allCatalogQuestions?.find((q) => q.id === questionId);

    const initialDisabledBusinessUnits = question?.disabledBusinessUnits || [];

    const initiallySelectedUnits = availableBusinessUnits.filter(
        (id) => !initialDisabledBusinessUnits.includes(id));

    const [enabledBusinessUnits, setEnabledBusinessUnits] = useState<string[]>(initiallySelectedUnits);
    const [disabledBusinessUnits, setDisabledBusinessUnits] = useState<string[]>(initialDisabledBusinessUnits);
    const [isNoUnitsSelected, setIsNoUnitsSelected] = useState<boolean>(
        initialDisabledBusinessUnits.length === allBusinessUnitIds.length);

    const handleBusinessUnitChange = (value: string[] | null) => {
        if (!value) {
            return;
        }

        const includesNoUnits = value.includes('no-units');
        const selectedUnits = value.filter((id) => id !== 'no-units');

        if (includesNoUnits) {
            if (!isNoUnitsSelected) {
                setIsNoUnitsSelected(true);
                setEnabledBusinessUnits([]);
                setDisabledBusinessUnits(availableBusinessUnits);
            } else {
                setIsNoUnitsSelected(false);
                setEnabledBusinessUnits(selectedUnits);
                setDisabledBusinessUnits(availableBusinessUnits.filter((id) => !selectedUnits.includes(id)));
            }
        } else {
            if (isNoUnitsSelected) {
                setIsNoUnitsSelected(false);
                setEnabledBusinessUnits(availableBusinessUnits);
                setDisabledBusinessUnits([]);
            } else {
                setEnabledBusinessUnits(selectedUnits);
                setDisabledBusinessUnits(availableBusinessUnits.filter((id) => !selectedUnits.includes(id)));
            }
        }
    };

    const handleSave = () => {
        const newlyUnselectedUnits = disabledBusinessUnits.filter((id) => initiallySelectedUnits.includes(id));
        const newlySelectedUnits = enabledBusinessUnits.filter((id) => !initiallySelectedUnits.includes(id));

        store.disableQuestionForBusinessUnit(newlyUnselectedUnits, questionId!, true);
        store.disableQuestionForBusinessUnit(newlySelectedUnits, questionId!, false);
    };

    const label = textInUserLanguage(
        userStore,
        store?.variables.catalogQuestionInEditing?.questionLanguages ?? ({} as Languages),
    );
    const unitLabel = `${store.label('questions.catalog-questions.sub-header.team-usage')} ${enabledBusinessUnits.length}/${availableBusinessUnits.length}`;

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{label}</h4>
            </ModalHeader>
            <ModalContent>
                <Select
                    label={unitLabel}
                    value={isNoUnitsSelected ? ['no-units'] : enabledBusinessUnits}
                    onChange={handleBusinessUnitChange}
                    options={[
                        { label: store.label('company.matrix-team-modal.no-children'), value: 'no-units' },
                        ...businessUnits
                            .filter((unit) => !disabledBusinessUnitsFromStore.includes(unit.id))
                            .map((unit) => ({
                                label: unit.name,
                                value: unit.id,
                            })),
                    ]}
                    search={{
                        label: store.label('common.filter.search.label'),
                        id: 'teams-search',
                        name: 'teams-search',
                    }}
                    id="child-teams"
                    name="child-teams"
                    type="multiple"
                />
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block color="white" text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block text={store.label('company.modal.save')} onClick={handleSave} />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
